import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Skeleton from '@mui/material/Skeleton'
import CircularProgress from '@mui/material/CircularProgress'
import PlayIcon from './assets/PlayIcon.svg?react'
import { useTranslate } from '@repo/unicity'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useVideoBanners } from '@/core/hooks'
import { isYouTubeUrl, toYouTubeEmbedUrl } from '@/core/services/videoBanner.service'

const IntroduceGlic = () => {
    const videoRef = useRef<HTMLVideoElement>(null)
    const hasLoadedRef = useRef(false)
    const [showPlayIcon, setShowPlayIcon] = useState(false)
    const [isVideoReady, setIsVideoReady] = useState(false)
    const { translate } = useTranslate()
    const { introduceBanner, isLoading: isLoadingBanner } = useVideoBanners()

    const hasCover = !!introduceBanner?.cover
    const hasVdo = !!introduceBanner?.vdo
    const isExternal = !!introduceBanner?.isExternal
    const externalLink = introduceBanner?.link ?? ''
    const isYouTube = hasVdo && !isExternal && isYouTubeUrl(introduceBanner!.vdo)
    // IntroduceGlic: ต้องกด play ก่อน จึงไม่ autoplay ใน embed URL
    const embedUrl = isYouTube ? toYouTubeEmbedUrl(introduceBanner!.vdo).replace('autoplay=1', 'autoplay=0') : ''
    const [iframeActive, setIframeActive] = useState(false)
    const [hideCover, setHideCover] = useState(false)
    const [isMuted, setIsMuted] = useState(true)
    const [showMuteBtn, setShowMuteBtn] = useState(false)
    const muteTimerRef = useRef<ReturnType<typeof setTimeout>>(null)

    const startMuteTimer = useCallback(() => {
        setShowMuteBtn(true)
        if (muteTimerRef.current) clearTimeout(muteTimerRef.current)
        muteTimerRef.current = setTimeout(() => setShowMuteBtn(false), 10_000)
    }, [])

    useEffect(() => {
        return () => {
            if (muteTimerRef.current) clearTimeout(muteTimerRef.current)
        }
    }, [])

    useEffect(() => {
        if (videoRef.current && hasVdo && !isYouTube && !hasLoadedRef.current) {
            hasLoadedRef.current = true
            videoRef.current.src = introduceBanner!.vdo
            videoRef.current.load()
        }
    }, [introduceBanner, hasVdo, isYouTube])

    const handleVideoReady = () => {
        setIsVideoReady(true)
        setShowPlayIcon(true)
    }

    // สำหรับ YouTube หรือ isExternal: แสดง play icon ทันทีที่ banner data พร้อม
    useEffect(() => {
        if (isYouTube || (isExternal && externalLink)) setShowPlayIcon(true)
    }, [isYouTube, isExternal, externalLink])

    const showLoader = isLoadingBanner || (!isYouTube && !isExternal && hasVdo && !isVideoReady && !hasCover)

    return (
        <Stack bgcolor={'warning.light'} p={'48px 24px'} gap={6}>
            <Typography variant="h1" color="primary.main">
                {translate('glic_2027_title')}
            </Typography>
            <Stack
                height={'335px'}
                sx={{
                    overflow: 'hidden',
                    borderRadius: '8px',
                    width: '100%',
                    position: 'relative'
                }}>
                {showLoader && (
                    <Stack position="absolute" top={0} left={0} width="100%" height="100%" alignItems="center" justifyContent="center" zIndex={1}>
                        <Skeleton
                            variant="rectangular"
                            width="100%"
                            height="100%"
                            animation="pulse"
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                bgcolor: '#fcf1e5',
                                '&::after': {
                                    background: 'linear-gradient(90deg, transparent, #b3d5f1, transparent)'
                                }
                            }}
                        />
                        <CircularProgress />
                    </Stack>
                )}

                {introduceBanner && (
                    <>
                        {/* Cover image — ซ่อนเมื่อกดเล่นวิดีโอ */}
                        {hasCover && !hideCover && (
                            <Box
                                component="img"
                                src={introduceBanner.cover}
                                width="100%"
                                height="100%"
                                sx={{ objectFit: 'cover', display: 'block', position: 'absolute', top: 0, left: 0 }}
                            />
                        )}

                        {/* YouTube iframe — แสดงหลังกด play */}
                        {isYouTube && iframeActive && (
                            <Box
                                component="iframe"
                                src={`${embedUrl.replace('autoplay=0', 'autoplay=1')}&mute=1`}
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    border: 'none'
                                }}
                                allow="autoplay; encrypted-media"
                                allowFullScreen
                            />
                        )}

                        {/* YouTube thumbnail ก่อนกด play (ไม่มี cover) */}
                        {isYouTube && !iframeActive && !hasCover && (
                            <Box
                                component="img"
                                src={`https://img.youtube.com/vi/${new URL(introduceBanner!.vdo).searchParams.get('v')}/hqdefault.jpg`}
                                width="100%"
                                height="100%"
                                sx={{ objectFit: 'cover', display: 'block', position: 'absolute', top: 0, left: 0 }}
                            />
                        )}

                        {/* Native video — ไม่แสดงเมื่อ isExternal */}
                        {hasVdo && !isYouTube && !isExternal && (
                            <video
                                ref={videoRef}
                                width="100%"
                                height="100%"
                                muted={isMuted}
                                autoPlay={false}
                                playsInline
                                preload="auto"
                                style={{
                                    position: hasCover ? 'absolute' : 'relative',
                                    top: 0,
                                    left: 0,
                                    opacity: isVideoReady ? (hasCover && !hideCover && showPlayIcon ? 0 : 1) : 0,
                                    transition: 'opacity 0.3s ease-in-out'
                                }}
                                onCanPlay={handleVideoReady}
                            />
                        )}

                        {/* Play icon — แสดงเสมอก่อนกด play */}
                        {(hasVdo || (isExternal && externalLink)) && showPlayIcon && !iframeActive && (
                            <Box
                                zIndex={2}
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    display: 'flex',
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    if (isExternal && externalLink) {
                                        window.open(externalLink, '_blank', 'noopener,noreferrer')
                                    } else if (isYouTube) {
                                        setIframeActive(true)
                                        setShowPlayIcon(false)
                                        setHideCover(true)
                                    } else if (videoRef.current) {
                                        videoRef.current.play()
                                        setShowPlayIcon(false)
                                        setHideCover(true)
                                        startMuteTimer()
                                    }
                                }}>
                                <PlayIcon />
                            </Box>
                        )}

                        {/* Video touch overlay — รับ touch/click เมื่อวิดีโอกำลังเล่น */}
                        {hasVdo && !isYouTube && !isExternal && !showPlayIcon && (
                            <Box
                                zIndex={2}
                                onClick={() => {
                                    if (showMuteBtn) {
                                        videoRef.current?.pause()
                                        setShowPlayIcon(true)
                                        setShowMuteBtn(false)
                                        if (muteTimerRef.current) clearTimeout(muteTimerRef.current)
                                    } else {
                                        startMuteTimer()
                                    }
                                }}
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    cursor: 'pointer'
                                }}
                            />
                        )}

                        {/* Mute/Unmute button */}
                        {hasVdo && !isYouTube && !isExternal && !showPlayIcon && (
                            <Box
                                zIndex={3}
                                onClick={e => {
                                    e.stopPropagation()
                                    setIsMuted(prev => !prev)
                                    startMuteTimer()
                                }}
                                sx={{
                                    position: 'absolute',
                                    bottom: 12,
                                    right: 12,
                                    width: 36,
                                    height: 36,
                                    borderRadius: '50%',
                                    bgcolor: 'rgba(0,0,0,0.5)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    opacity: showMuteBtn ? 1 : 0,
                                    pointerEvents: showMuteBtn ? 'auto' : 'none',
                                    transition: 'opacity 0.3s ease-in-out',
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                                }}>
                                <svg viewBox="0 0 24 24" width="20" height="20" fill="white">
                                    {isMuted ? (
                                        <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.796 8.796 0 0021 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06a10.95 10.95 0 003.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
                                    ) : (
                                        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                                    )}
                                </svg>
                            </Box>
                        )}
                    </>
                )}
            </Stack>
            <Stack gap={6}>
                <Typography variant="body1" color="primary.dark">
                    {translate('glic_2027_description_1')}
                </Typography>
                <Typography variant="body1" color="primary.dark">
                    {translate('glic_2027_description_2')}
                </Typography>
            </Stack>
        </Stack>
    )
}

export default IntroduceGlic
